import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getVideoBySlug, allVideos, getVideoUrl } from '../data/videoData';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import { isFullscreenActive, requestFullscreen, exitFullscreen } from '../lib/fullscreen';

const VideoWatch = () => {
  const { videoSlug } = useParams();
  const navigate = useNavigate();
  const video = getVideoBySlug(videoSlug);
  
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [realDuration, setRealDuration] = useState(null);

  // YouTube-style autoplay state (persisted in localStorage)
  const [autoplay, setAutoplay] = useState(() => {
    try {
      const saved = localStorage.getItem('mediccare_video_autoplay');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  const [countdown, setCountdown] = useState(null); // { seconds: number, nextVideo: object }
  const [endScreen, setEndScreen] = useState(null);
  const playerBoxRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const wasFullscreenRef = useRef(false);

  // Fullscreen toggle
  const toggleFullscreen = (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }

    const isFs = isFullscreenActive();

    if (isFs) {
      exitFullscreen(videoRef.current);
    } else {
      requestFullscreen(playerBoxRef.current, videoRef.current);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = isFullscreenActive();
      setIsFullscreen(isFs);
      wasFullscreenRef.current = isFs;
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleAutoplay = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setAutoplay(prev => {
      const next = !prev;
      try {
        localStorage.setItem('mediccare_video_autoplay', String(next));
      } catch {}
      return next;
    });
  };

  // Set document metadata for SEO and rich share previews
  useDocumentMetadata({
    title: video 
      ? `${video.title} | ${video.productName} Video | MedicCare`
      : 'Video | MedicCare',
    description: video?.description || 'Se instruktions- og præsentationsvideoer fra MedicCare.',
    image: video?.poster
  });

  // Autoplay or reset when video slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsPlaying(false);
    setCountdown(null);
    setEndScreen(null);
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoSlug]);

  // Countdown timer effect
  useEffect(() => {
    if (!countdown) return;

    if (countdown.seconds <= 0) {
      const next = countdown.nextVideo;
      setCountdown(null);
      setEndScreen(null);
      if (next) {
        navigate(`/video/${next.slug}`);
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(prev => prev ? { ...prev, seconds: prev.seconds - 1 } : null);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  const handleCopyLink = () => {
    const fullUrl = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }).catch(() => {
        fallbackCopy(fullUrl);
      });
    } else {
      fallbackCopy(fullUrl);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      prompt('Kopiér linket herunder:', text);
    }
  };

  const handlePlayToggle = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  };

  // 404 Video not found state
  if (!video) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-slate-50 px-4 py-20">
        <div className="max-w-lg text-center bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-slate-100">
          <div className="w-16 h-16 bg-amber-100 text-amber-700 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl">
            <i className="fa-light fa-video-slash" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
            Videoen blev ikke fundet
          </h1>
          <p className="text-slate-600 mb-8 leading-relaxed text-sm">
            Det link du har fulgt findes ikke længere, eller adressen er tastet forkert. Du kan finde alle vores instruktions- og præsentationsvideoer i oversigten.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/video"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2c3223] text-white font-semibold rounded-xl hover:bg-[#3d4531] transition-colors text-sm shadow-md"
            >
              <i className="fa-light fa-play" /> Se alle videoer
            </Link>
            <Link
              to="/medimi-smart/tim"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-colors text-sm"
            >
              Gå til TIM-løsningen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Get related and sequence videos
  const productVideos = allVideos.filter(v => v.productId === video.productId);
  const currentIndex = productVideos.findIndex(v => v.id === video.id);
  const nextVideo = productVideos[currentIndex !== -1 ? (currentIndex + 1) % productVideos.length : 0];
  const prevVideo = productVideos[currentIndex !== -1 ? (currentIndex - 1 + productVideos.length) % productVideos.length : 0];

  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (!nextVideo) return;

    if (autoplay) {
      setCountdown({
        seconds: 5,
        nextVideo
      });
    } else {
      setEndScreen({
        nextVideo
      });
    }
  };

  const cancelCountdown = () => {
    if (countdown) {
      setEndScreen({ nextVideo: countdown.nextVideo });
      setCountdown(null);
    }
  };

  const playNextNow = () => {
    const next = countdown?.nextVideo || endScreen?.nextVideo || nextVideo;
    setCountdown(null);
    setEndScreen(null);
    if (next) {
      navigate(`/video/${next.slug}`);
    }
  };

  const replayVideo = () => {
    setCountdown(null);
    setEndScreen(null);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const relatedVideos = allVideos
    .filter(v => v.id !== video.id && v.productId === video.productId)
    .slice(0, 4);

  const otherProductsVideos = allVideos
    .filter(v => v.productId !== video.productId)
    .slice(0, 3);

  return (
    <div className="bg-slate-50 min-h-screen py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap" aria-label="Brødkrumme">
          <Link to="/" className="hover:text-slate-900 transition-colors">
            Forside
          </Link>
          <i className="fa-regular fa-chevron-right text-[10px] text-slate-400" />
          <Link to="/video" className="hover:text-slate-900 transition-colors">
            Videoer
          </Link>
          <i className="fa-regular fa-chevron-right text-[10px] text-slate-400" />
          <Link 
            to={`${video.productPath}#video-section`} 
            className="hover:text-slate-900 transition-colors"
          >
            {video.productName}
          </Link>
          <i className="fa-regular fa-chevron-right text-[10px] text-slate-400" />
          <span className="text-slate-900 font-medium truncate max-w-[240px] sm:max-w-none">
            {video.title}
          </span>
        </nav>

        {/* Main Video Theater Card */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-slate-100 mb-12">
          
          {/* Video Player Box */}
          <div 
            ref={playerBoxRef}
            onDoubleClick={toggleFullscreen}
            className={`relative bg-black group overflow-hidden flex items-center justify-center ${
              isFullscreen ? 'w-full h-full' : 'aspect-video'
            }`}
          >
            <video
              ref={videoRef}
              src={video.url}
              poster={video.poster}
              controls
              playsInline
              preload="metadata"
              className={`w-full h-full ${isFullscreen ? 'object-contain' : 'object-cover'}`}
              onPlay={() => {
                setIsPlaying(true);
                if (videoRef.current?.textTracks?.[0]) {
                  videoRef.current.textTracks[0].mode = 'showing';
                }
              }}
              onLoadedMetadata={(e) => {
                const el = e.currentTarget;
                if (el?.duration && isFinite(el.duration)) {
                  const mins = Math.floor(el.duration / 60);
                  const secs = Math.floor(el.duration % 60);
                  setRealDuration(`${mins}:${secs.toString().padStart(2, '0')}`);
                }
                if (el?.textTracks?.[0]) {
                  el.textTracks[0].mode = 'showing';
                }
              }}
              onPause={() => setIsPlaying(false)}
              onEnded={handleVideoEnded}
            >
              {video.subtitle && (
                <track
                  key={video.subtitle}
                  kind="subtitles"
                  src={video.subtitle}
                  srcLang="da"
                  label="Dansk"
                  default
                />
              )}
            </video>

            {/* Custom Overlay when paused/not started */}
            {!isPlaying && !countdown && !endScreen && (
              <div 
                onClick={handlePlayToggle}
                className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/40 hover:bg-slate-950/50 transition-all cursor-pointer z-10"
              >
                <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-white text-[#2c3223] rounded-full shadow-2xl transform hover:scale-110 transition-all duration-300">
                  <i className="fa-solid fa-play text-2xl md:text-3xl ml-1" />
                </div>
                <div className="mt-4 flex items-center gap-2 text-white font-semibold text-sm tracking-wide bg-slate-900/80 backdrop-blur-md px-5 py-2 rounded-full shadow-lg">
                  <span>Start video</span>
                  <span className="text-emerald-400">({realDuration || video.duration})</span>
                </div>
              </div>
            )}

            {/* YouTube Countdown Overlay */}
            {countdown && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-950/90 text-white p-4 sm:p-6 backdrop-blur-md animate-in fade-in duration-200 select-none">
                <div className="flex flex-col items-center text-center mb-4 sm:mb-5 max-w-md">
                  <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-2">
                    <svg className="w-14 h-14 sm:w-16 sm:h-16 transform -rotate-90" viewBox="0 0 36 36">
                      <path
                        className="text-white/20"
                        strokeWidth="3.5"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className="text-emerald-400 transition-all duration-1000 ease-linear"
                        strokeDasharray="100, 100"
                        strokeDashoffset={`${((5 - countdown.seconds) / 5) * 100}`}
                        strokeLinecap="round"
                        strokeWidth="3.5"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="absolute font-bold text-xl sm:text-2xl text-white">
                      {countdown.seconds}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-semibold mb-1">
                    <i className="fa-solid fa-forward-step text-[10px]" />
                    Autoafspilning aktiv
                  </div>
                  <h4 className="text-sm sm:text-base md:text-lg font-bold text-white">
                    Næste video starter om {countdown.seconds} {countdown.seconds === 1 ? 'sekund' : 'sekunder'}
                  </h4>
                </div>

                {countdown.nextVideo && (
                  <div 
                    onClick={playNextNow}
                    className="w-full max-w-md bg-white/10 hover:bg-white/15 border border-white/15 rounded-2xl p-3 sm:p-3.5 flex items-center gap-3.5 cursor-pointer transition-all duration-200 group mb-4 sm:mb-5 shadow-2xl"
                    title="Klik for at afspille med det samme"
                  >
                    <div className="relative w-24 sm:w-28 aspect-video bg-black rounded-xl overflow-hidden shrink-0 border border-white/10">
                      <img
                        src={countdown.nextVideo.poster}
                        alt={countdown.nextVideo.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/95 text-slate-900 flex items-center justify-center group-hover:scale-110 group-hover:bg-emerald-400 transition-all shadow-md">
                          <i className="fa-solid fa-play text-[10px] sm:text-[11px] ml-0.5" />
                        </div>
                      </div>
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/80 text-white">
                        {countdown.nextVideo.duration} min
                      </span>
                    </div>

                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-0.5 block">
                        Næste i rækkefølgen
                      </span>
                      <h5 className="font-bold text-xs sm:text-sm text-white line-clamp-1 group-hover:text-emerald-300 transition-colors">
                        {countdown.nextVideo.title}
                      </h5>
                      <p className="text-[11px] sm:text-xs text-white/70 line-clamp-2 mt-0.5 leading-relaxed">
                        {countdown.nextVideo.description}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  <button
                    type="button"
                    onClick={playNextNow}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition-all shadow-lg hover:scale-105 cursor-pointer"
                  >
                    <i className="fa-solid fa-play text-xs" />
                    <span>Afspil nu</span>
                  </button>

                  <button
                    type="button"
                    onClick={cancelCountdown}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-xs bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20 cursor-pointer"
                  >
                    <i className="fa-solid fa-xmark text-xs" />
                    <span>Annuller</span>
                  </button>

                  <button
                    type="button"
                    onClick={replayVideo}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-xs text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <i className="fa-solid fa-rotate-left text-xs" />
                    <span>Se igen</span>
                  </button>
                </div>
              </div>
            )}

            {/* End Screen Overlay when Autoplay is OFF */}
            {endScreen && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-950/85 text-white p-4 sm:p-6 backdrop-blur-md animate-in fade-in duration-200 select-none">
                <div className="text-center mb-4 sm:mb-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-2 text-white">
                    <i className="fa-solid fa-check text-sm sm:text-base text-emerald-400" />
                  </div>
                  <h4 className="text-sm sm:text-base md:text-lg font-bold text-white">
                    Video afsluttet
                  </h4>
                  <p className="text-xs text-slate-300 mt-1">
                    Autoafspilning er slået fra
                  </p>
                </div>

                {endScreen.nextVideo && (
                  <div 
                    onClick={playNextNow}
                    className="w-full max-w-md bg-white/10 hover:bg-white/15 border border-white/15 rounded-2xl p-3 sm:p-3.5 flex items-center gap-3.5 cursor-pointer transition-all duration-200 group mb-4 sm:mb-5 shadow-2xl"
                  >
                    <div className="relative w-24 sm:w-28 aspect-video bg-black rounded-xl overflow-hidden shrink-0 border border-white/10">
                      <img
                        src={endScreen.nextVideo.poster}
                        alt={endScreen.nextVideo.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white text-slate-900 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <i className="fa-solid fa-play text-[10px] sm:text-[11px] ml-0.5" />
                        </div>
                      </div>
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/80 text-white">
                        {endScreen.nextVideo.duration} min
                      </span>
                    </div>

                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5 block">
                        Næste video i serien
                      </span>
                      <h5 className="font-bold text-xs sm:text-sm text-white line-clamp-1 group-hover:text-emerald-300 transition-colors">
                        {endScreen.nextVideo.title}
                      </h5>
                      <p className="text-[11px] sm:text-xs text-white/70 line-clamp-2 mt-0.5 leading-relaxed">
                        {endScreen.nextVideo.description}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  {endScreen.nextVideo && (
                    <button
                      type="button"
                      onClick={playNextNow}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition-all shadow-lg cursor-pointer"
                    >
                      <i className="fa-solid fa-forward-step text-xs" />
                      <span>Afspil næste video</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={replayVideo}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-xs bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20 cursor-pointer"
                  >
                    <i className="fa-solid fa-rotate-left text-xs" />
                    <span>Se forfra</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Details & Action Bar below video */}
          <div className="p-6 md:p-10">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 pb-8 border-b border-slate-100">
              <div className="space-y-3 max-w-3xl">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider bg-brand-soft text-[#2c3223] rounded-lg">
                    <i className="fa-light fa-layer-group text-xs" /> {video.productName}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold bg-slate-100 text-slate-600 rounded-lg">
                    <i className="fa-light fa-tag text-xs" /> {video.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-slate-500 font-medium">
                    <i className="fa-light fa-clock text-xs" /> {realDuration || video.duration} min
                  </span>
                  {video.subtitle && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg">
                      <i className="fa-solid fa-closed-captioning text-emerald-600" />
                      <span>Undertekster (Dansk)</span>
                    </span>
                  )}
                </div>

                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                  {video.title}
                </h1>

                <p className="text-base sm:text-lg text-slate-600 leading-relaxed pt-1">
                  {video.longDescription || video.description}
                </p>

                {/* Autoplay & Next indicator */}
                <div className="pt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                  <span className="font-semibold text-slate-700 flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-lg">
                    <i className="fa-solid fa-list-ol text-slate-500" />
                    Video {currentIndex + 1} af {productVideos.length}
                  </span>

                  {autoplay && nextVideo && (
                    <span className="text-emerald-700 font-medium flex items-center gap-1.5 bg-emerald-50 border border-emerald-200/60 px-3 py-1.5 rounded-lg">
                      <i className="fa-solid fa-forward-step text-[11px]" />
                      Næste i rækkefølgen: <strong className="font-semibold">{nextVideo.title}</strong>
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons: Autoplay Switch, Prev/Next, Copy Link & Share */}
              <div className="flex flex-col gap-3 shrink-0">
                {/* Autoplay Toggle Switch */}
                <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-xl">
                  <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <i className={`fa-solid ${autoplay ? 'fa-forward-step text-emerald-600' : 'fa-pause text-slate-400'}`} />
                    Autoafspilning:
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={autoplay}
                      onClick={toggleAutoplay}
                      className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        autoplay ? 'bg-[#2c3223]' : 'bg-slate-300'
                      }`}
                      title={autoplay ? 'Autoafspilning er til (klik for at slå fra)' : 'Autoafspilning er fra (klik for at slå til)'}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-xs transition duration-200 ease-in-out ${
                          autoplay ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      autoplay ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {autoplay ? 'TIL' : 'FRA'}
                    </span>
                  </div>
                </div>

                {/* Previous / Next buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      cancelCountdown();
                      setEndScreen(null);
                      navigate(`/video/${prevVideo.slug}`);
                    }}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                    title="Forrige video i serien"
                  >
                    <i className="fa-solid fa-backward-step text-xs" />
                    <span>Forrige</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      cancelCountdown();
                      setEndScreen(null);
                      navigate(`/video/${nextVideo.slug}`);
                    }}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                    title="Næste video i serien"
                  >
                    <span>Næste</span>
                    <i className="fa-solid fa-forward-step text-xs" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 shadow-xs border cursor-pointer ${
                    isFullscreen
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                      : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200 hover:border-slate-300'
                  }`}
                  title={isFullscreen ? 'Afslut stor skærm (Esc / F)' : 'Åbn i stor skærm (F)'}
                >
                  <i className={`fa-solid ${isFullscreen ? 'fa-compress text-emerald-600' : 'fa-expand text-slate-500'}`} />
                  <span>{isFullscreen ? 'Afslut stor skærm' : 'Stor skærm'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`inline-flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 shadow-xs border cursor-pointer ${
                    copied 
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-md ring-2 ring-emerald-300'
                      : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200 hover:border-slate-300'
                  }`}
                  title="Kopiér link til at dele denne video"
                >
                  <i className={`fa-solid ${copied ? 'fa-check text-white' : 'fa-link text-slate-500'}`} />
                  <span>{copied ? 'Link kopieret!' : 'Kopiér videolink'}</span>
                </button>

                <Link
                  to={`${video.productPath}#video-section`}
                  className="inline-flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm bg-[#2c3223] text-white hover:bg-[#3d4531] transition-colors shadow-sm"
                >
                  <i className="fa-light fa-arrow-left" />
                  <span>Gå til {video.productName}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Related Videos Section */}
        {relatedVideos.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
                  Flere videoer om {video.productName}
                </h2>
                <p className="text-xs md:text-sm text-slate-500 mt-1">
                  Højreklik på en video for at åbne i en ny fane eller kopiere linket
                </p>
              </div>
              <Link 
                to={`${video.productPath}#video-section`}
                className="text-xs md:text-sm font-bold text-brand-dark hover:underline flex items-center gap-1.5"
              >
                Se alle i sektionen <i className="fa-regular fa-arrow-right text-xs" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedVideos.map((relVideo) => (
                <Link
                  key={relVideo.id}
                  to={getVideoUrl(relVideo)}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all duration-300 flex flex-col"
                >
                  <div className="relative aspect-video bg-slate-100 overflow-hidden">
                    <img
                      src={relVideo.poster}
                      alt={relVideo.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-slate-950/40 transition-colors flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <i className="fa-solid fa-play text-xs ml-0.5 text-[#2c3223]" />
                      </div>
                    </div>
                    <span className="absolute bottom-2 right-2 bg-slate-950/80 text-white text-[10px] font-semibold px-2 py-0.5 rounded backdrop-blur-xs">
                      {relVideo.duration}
                    </span>
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                        {relVideo.category}
                      </span>
                      <h3 className="font-bold text-sm text-slate-900 group-hover:text-brand-dark transition-colors line-clamp-2 leading-snug">
                        {relVideo.title}
                      </h3>
                      <p className="text-slate-500 text-xs mt-1.5 line-clamp-2 leading-relaxed">
                        {relVideo.description}
                      </p>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-50 flex items-center justify-between text-[11px] text-slate-400">
                      <span>Åbn video</span>
                      <i className="fa-regular fa-arrow-right group-hover:translate-x-1 transition-transform text-brand-dark" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Other Solutions Videos */}
        {otherProductsVideos.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 tracking-tight">
              Andre videoer &amp; løsninger
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {otherProductsVideos.map((otherVideo) => (
                <Link
                  key={otherVideo.id}
                  to={getVideoUrl(otherVideo)}
                  className="group bg-white p-4 rounded-2xl border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all flex gap-4 items-center"
                >
                  <div className="relative w-24 h-16 rounded-xl overflow-hidden shrink-0 bg-slate-100">
                    <img 
                      src={otherVideo.poster} 
                      alt={otherVideo.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-slate-950/20 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-white text-[#2c3223] flex items-center justify-center text-[9px] shadow-sm">
                        <i className="fa-solid fa-play ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] font-bold text-brand-dark block truncate">
                      {otherVideo.productName}
                    </span>
                    <h3 className="font-bold text-xs text-slate-900 group-hover:text-brand-dark truncate mt-0.5">
                      {otherVideo.title}
                    </h3>
                    <span className="text-[10px] text-slate-400 block mt-1">
                      {otherVideo.duration} min
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VideoWatch;
